// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Internal Packages
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";

// External Components
import { MdContacts, MdRefresh } from "react-icons/md";

// Internal Components
import SearchBar from "../UI/SearchBar";
import DateFilter from "../UI/DateFilter";
import Heading from "../UI/Heading";
import FilterMenu from "../UI/FilterMenu";
import PaginationMenu from "../UI/PaginationMenu";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import DomainSwitch from "../UI/DomainSwitch";
import { isoDateToString } from "../../util/format-date";
import BarGraph from "../UI/BarGraph";
import LoaderGraph from "../UI/LoaderGraph";
import { endOfWeek, lastDayOfWeek, startOfWeek } from "date-fns";
import DetailPair from "../UI/DetailPair";
import PersonCard from "../UI/PersonCard";

/** ========================================================================
 * * Opportunities Report Page
 * Renders a page, which displays a summary of sales opportunities.
 * ========================================================================== */

export default function OpportunitiesReport() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search); // Create Empty Graph Data
  // Default Filters
  const defaultFilters = {
    sd: startOfWeek(new Date()).toISOString(),
    ed: endOfWeek(new Date()).toISOString(),
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [opportunities, setOpportunities] = useState([]);
  const [report, setReport] = useState({
    graph: [],
    opportunities: 0,
    new: 0,
    nurture: 0,
    booked: 0,
    subscribed: 0,
    abandoned: 0,
    employees: {},
  });
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(false);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      let newReport = {
        graph: [],
        opportunities: 0,
        new: 0,
        nurture: 0,
        booked: 0,
        subscribed: 0,
        abandoned: 0,
        employees: {},
      };
      for (let i = 6; i >= 0; i--) {
        const lastDayOfWeek = endOfWeek(new Date());
        lastDayOfWeek.setDate(lastDayOfWeek.getDate() - i);
        const entry = {
          name: lastDayOfWeek.toLocaleDateString("en-US", {
            weekday: "short",
          }),
          opportunities: 0,
          won: 0,
        };
        newReport.graph.push(entry);
      }
      const employees = await requestAdminAPI("filter", {
        table: "employee",
        filters: [`active: { eq: true }`, `ghlId: { ne: null }`],
      });
      // Add Employees to Report
      employees.forEach((emp) => {
        newReport.employees[emp.ghlId] = {
          id: emp.id,
          ghlId: emp.ghlId,
          opportunities: 0,
          new: 0,
          nurture: 0,
          booked: 0,
          subscribed: 0,
          abandoned: 0,
        };
      });

      let filters = { ...filter, limit: null };
      if (!filters.sd || !filters.ed) filters.sd = filters.ed = "";

      // Send Request
      const data = await requestAdminAPI("fetch-opportunities", filters);
      // Replace URL
      let url = "/opportunities/report?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      // Populate Graph Data
      data.forEach((opp) => {
        // Find the corresponding entry in the graph
        newReport.graph[new Date(opp.createdAt).getDay()].opportunities += 1;
        newReport.opportunities += 1;
        if (newReport.employees[opp.assignedTo])
          newReport.employees[opp.assignedTo].opportunities += 1;

        // By Stage
        if (opp.stage?.name === "Nurture") {
          newReport.nurture += 1;
          if (newReport.employees[opp.assignedTo])
            newReport.employees[opp.assignedTo].nurture += 1;
        } else if (opp.stage?.name === "Abandon") {
          newReport.abandoned += 1;
          if (newReport.employees[opp.assignedTo])
            newReport.employees[opp.assignedTo].abandoned += 1;
        } else if (opp.stage?.name === "New Lead") {
          newReport.new += 1;
          if (newReport.employees[opp.assignedTo])
            newReport.employees[opp.assignedTo].new += 1;
        } else if (opp.stage?.name === "Booked") {
          newReport.booked += 1;
          newReport.graph[new Date(opp.createdAt).getDay()].won += 1;
          if (newReport.employees[opp.assignedTo])
            newReport.employees[opp.assignedTo].booked += 1;
        } else if (opp.stage?.name === "Subscribed") {
          if (newReport.employees[opp.assignedTo])
            newReport.employees[opp.assignedTo].subscribed += 1;
          newReport.subscribed += 1;
          newReport.graph[new Date(opp.createdAt).getDay()].won += 1;
        }
      });

      setReport(newReport);
      setEmployees(employees);
      setOpportunities(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container grid">
      <Heading
        title="Opportunities Report"
        isBackArrow={true}
        text="The graph below sums the opportunities created for each day of week during the selected date range."
      >
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
          defaultPeriod="Week"
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
      </Heading>

      <div className="grid gap-4 lg:flex lg:space-x-4 lg:gap-0">
        {loading ? (
          <LoaderGraph />
        ) : (
          <BarGraph
            data={report.graph}
            primary="opportunities"
            secondaryFill="#15803d"
            secondary="won"
          />
        )}
        <div className="grid gap-2 text-sm min-w-[200px]">
          <DetailPair
            label="Total"
            value={report.opportunities}
            loading={loading}
          />
          <DetailPair label="New Leads" value={report.new} loading={loading} />
          <DetailPair
            label="Nurture"
            value={report.nurture}
            loading={loading}
          />
          <DetailPair
            label="Abandoned"
            value={report.abandoned}
            loading={loading}
          />
          <DetailPair label="Booked" value={report.booked} loading={loading} />
          <DetailPair
            label="Subscribed"
            value={report.subscribed}
            loading={loading}
          />
        </div>
      </div>
      <div className="grid gap-6 text-sm">
        {employees.map((employee, idx) => (
          <div
            key={idx}
            className="grid gap-4 pb-4 border-b lg:grid-cols-4 border-stone-600"
          >
            <PersonCard
              name={employee.name}
              image={employee.photo}
              label={employee.type}
              secondary={employee.ghlId}
              link={`/employees/info?id=${employee.id}`}
            />
            <div className="grid gap-2">
              <DetailPair
                label="Assigned"
                value={report.employees[employee.ghlId]?.opportunities}
                loading={loading}
              />
              <DetailPair
                label="New Leads"
                value={report.employees[employee.ghlId]?.new}
                loading={loading}
              />
            </div>
            <div className="grid gap-2">
              <DetailPair
                label="Nurture"
                value={report.employees[employee.ghlId]?.nurture}
                loading={loading}
              />
              <DetailPair
                label="Abandoned"
                value={report.employees[employee.ghlId]?.abandoned}
                loading={loading}
              />
            </div>
            <div className="grid gap-2">
              <DetailPair
                label="Booked"
                value={report.employees[employee.ghlId]?.booked}
                loading={loading}
              />
              <DetailPair
                label="Subscribed"
                value={report.employees[employee.ghlId]?.subscribed}
                loading={loading}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
